import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { CalendarEventDto } from "components/visits/types";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { agendaSlice } from "store/slices/agenda";
import { AgendaDueRow, computeNewlyCompleted } from "./marketing-plan-agenda";

interface AgendaSnoozeRow {
    contactId: string;
    touchpointId: string | null;
}

export const useAgendaInvalidation = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useCallback(async () => {
        const dueKey = trpcClient.marketingPlaybook.dueTouchpoints.queryKey();
        const snoozeKey = trpcClient.marketingPlaybook.activeSnoozes.queryKey();
        const calendarKey = trpcClient.calendarEvents.listForCalendar.queryKey();
        const contactPlanKey = trpcClient.marketingPlaybook.contactPlan.queryKey();
        const readDue = (): AgendaDueRow[] => {
            const query = queryClient
                .getQueryCache()
                .findAll({ queryKey: dueKey, type: "active" })[0];
            return (query?.state.data as AgendaDueRow[] | undefined) ?? [];
        };
        const readUpcomingVisitContactIds = (): Set<string> => {
            const query = queryClient
                .getQueryCache()
                .findAll({ queryKey: calendarKey, type: "active" })[0];
            const events = (query?.state.data as CalendarEventDto[] | undefined) ?? [];
            return new Set(events.map(event => event.parentId));
        };
        const before = readDue();
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: dueKey }),
            queryClient.invalidateQueries({ queryKey: snoozeKey }),
            queryClient.invalidateQueries({ queryKey: calendarKey }),
            queryClient.invalidateQueries({ queryKey: contactPlanKey }),
        ]);
        const after = readDue();
        const snoozeQuery = queryClient
            .getQueryCache()
            .findAll({ queryKey: snoozeKey, type: "active" })[0];
        const snoozes = (snoozeQuery?.state.data as AgendaSnoozeRow[] | undefined) ?? [];
        const completed = computeNewlyCompleted(
            before,
            after,
            snoozes.map(snooze => ({
                contactId: snooze.contactId,
                touchpointId: snooze.touchpointId,
            })),
            readUpcomingVisitContactIds(),
        );
        if (completed.length > 0) {
            dispatch(agendaSlice.actions.reportRecentlyCompleted(completed));
        }
    }, [trpcClient, queryClient, dispatch]);
};
